import axios from "axios";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  Organization,
  Site,
} from "../FloorplanEditor/ImportFloorplanModal/OpenSpaceSheetImportPanel";

// Fetches the OpenSpace organization and site lists for the region → client →
// project cascade. Selection state stays with the caller (e.g. an antd form);
// this hook only owns the fetched lists and their loading/error state.
// No project context is sent: this hook serves project creation, where the
// project doesn't exist yet, so the mapping service uses the Janus bot token.
export const useOpenSpaceOrgSites = () => {
  const client = useMemo(
    () =>
      axios.create({
        baseURL: `${process.env["OPENSPACE_MAPPING_SERVICE"]}`,
        timeout: 30000,
        withCredentials: true,
      }),
    [],
  );

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [siteLoading, setSiteLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped on every fetch/reset; responses from older requests are discarded
  // so a slow response can't overwrite the result of a newer selection.
  const requestIdRef = useRef(0);

  const fetchOrganizations = useCallback(
    async (region: string) => {
      const requestId = ++requestIdRef.current;
      setOrganizations([]);
      setSites([]);
      setError(null);
      setOrgLoading(true);
      try {
        const response = await client.get(`/get-organization`, {
          params: { region },
        });
        if (requestId !== requestIdRef.current) {
          return;
        }
        setOrganizations(response.data.content);
      } catch (err) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setError(
          axios.isAxiosError(err)
            ? `Error fetching organizations: ${
                err.response?.data?.detail || err.message
              }`
            : "An unexpected error occurred while fetching organizations.",
        );
      } finally {
        if (requestId === requestIdRef.current) {
          setOrgLoading(false);
        }
      }
    },
    [client],
  );

  const fetchSites = useCallback(
    async (organizationId: string, region: string) => {
      const requestId = ++requestIdRef.current;
      setSites([]);
      setError(null);
      setSiteLoading(true);
      try {
        const response = await client.get(`/get-sites/${organizationId}`, {
          params: { region },
        });
        if (requestId !== requestIdRef.current) {
          return;
        }
        setSites(response.data.content);
      } catch (err) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setError(
          axios.isAxiosError(err)
            ? `Error fetching sites: ${
                err.response?.data?.detail || err.message
              }`
            : "An unexpected error occurred while fetching sites.",
        );
      } finally {
        if (requestId === requestIdRef.current) {
          setSiteLoading(false);
        }
      }
    },
    [client],
  );

  const reset = useCallback(() => {
    requestIdRef.current++;
    setOrganizations([]);
    setSites([]);
    setError(null);
    setOrgLoading(false);
    setSiteLoading(false);
  }, []);

  return {
    organizations,
    sites,
    orgLoading,
    siteLoading,
    error,
    fetchOrganizations,
    fetchSites,
    reset,
  };
};
